import i18n from '../i18n';
import * as models from 'smg/admin/models';
import * as template from 'smg/template';
import { DollarFormat } from 'smg/util';
import Big from 'big.js';

export interface PrepaymentDeps {
    // Fires when the admin toggles a deposit row's checkbox. `checkedPaid` is
    // the uncapped sum of the checked deposits' paid amounts; undefined means
    // every deposit is checked (the default), which tells the summary to keep
    // using the server-capped cart `prepayment` untouched — the default
    // selection stays byte-for-byte today's behavior.
    onSelectionChange(checkedPaid: Big | undefined): void;
}

export interface PrepaymentHandle {
    // Replace the rendered prepayment list with the cart's latest deposit
    // orders. Empty `ordersInit` hides the section entirely. `appliedInit` is
    // the cart-response `prepayment` amount — the server-capped value that
    // will actually be transferred at place-order, which may be less than the
    // sum of available deposits when the cart's preTotal is below them.
    // Called once at page load; the deposit list is fixed for the session.
    refresh(ordersInit: models.ICartPendingDepositOrder[] | undefined, appliedInit: Big): void;
    // Update just the "Applied to this order" header. The cap moves with
    // preTotal (shipping/promo/discount changes) and the checkbox selection,
    // so the summary pushes the fresh capped value here on every recompute.
    setApplied(applied: Big): void;
    // Deposit order ids the admin unchecked. Place-order sends these as
    // skipDepositOrderIDs so the server's deposit transfer leaves them live
    // (payments and all) for a future order.
    skippedDepositOrderIDs(): string[];
}

export function SetupPrepayment(deps: PrepaymentDeps): PrepaymentHandle {
    const $section = document.getElementById('checkout-section-prepayment') as HTMLElement;
    const $total = document.getElementById('prepayment-total') as HTMLElement;
    const $list = document.getElementById('prepayment-list') as HTMLElement;

    // The rendered deposit orders and the unchecked (skipped) ones. The list
    // renders once at page load, so both live for the whole session.
    let orders: models.CartPendingDepositOrder[] = [];
    const skipped = new Set<string>();

    function setApplied(applied: Big) {
        $total.textContent = DollarFormat(applied);
    }

    function notifySelection() {
        if (skipped.size === 0) {
            deps.onSelectionChange(undefined);
            return;
        }
        let checkedPaid = Big(0);
        for (const order of orders) {
            if (!skipped.has(order.id)) {
                checkedPaid = checkedPaid.plus(order.paid);
            }
        }
        deps.onSelectionChange(checkedPaid);
    }

    function render(ordersInit: models.ICartPendingDepositOrder[] | undefined, applied: Big) {
        skipped.clear();
        if (!ordersInit || ordersInit.length === 0) {
            orders = [];
            $section.classList.add('d-none');
            $list.replaceChildren();
            notifySelection();
            return;
        }

        orders = ordersInit.map(o => new models.CartPendingDepositOrder(o));
        setApplied(applied);

        const f = document.createDocumentFragment();
        for (const order of orders) {
            const $row = template.Clone('#prepayment-row-template').firstElementChild as HTMLElement;

            const $invoice = $row.querySelector('.prepayment-row-invoice') as HTMLAnchorElement;
            if (order.invoiceNumber > 0) {
                $invoice.textContent = i18n('invoiceN').replace('%s', String(order.invoiceNumber ?? ''));
                $invoice.href = order.iapURL();
            } else {
                // Deposit order created but invoice number not yet assigned
                // (rare — Normalize backfills it on insert, but be safe).
                $invoice.textContent = i18n('depositOrder');
                $invoice.removeAttribute('href');
                $invoice.classList.remove('text-decoration-none');
            }

            const $date = $row.querySelector('.prepayment-row-date') as HTMLElement;
            $date.textContent = order.created.isValid ?
                order.created.toLocaleString({ month: 'short', day: 'numeric', year: 'numeric' }) :
                '';

            // Flag whether this deposit is from the project being checked out or
            // pulled from another of the customer's projects, so the admin sees
            // at a glance where the credit is coming from. Other-project gets a
            // warning-toned badge with the project name; same-project stays quiet.
            const $project = $row.querySelector('.prepayment-row-project') as HTMLElement;
            if (order.sameProject) {
                $project.textContent = i18n('thisProject');
                $project.className = 'prepayment-row-project small text-muted';
            } else {
                $project.textContent = order.projectName ? i18n('otherProjectNamed').replace('%s', order.projectName) : i18n('otherProject');
                $project.className = 'prepayment-row-project badge rounded-pill text-bg-warning-subtle text-warning-emphasis text-wrap text-start';
            }

            const $info = $row.querySelector('.prepayment-row-info') as HTMLElement;
            const $amount = $row.querySelector('.prepayment-row-amount') as HTMLElement;
            $amount.textContent = DollarFormat(order.paid);

            const $checkbox = $row.querySelector('.prepayment-row-checkbox') as HTMLInputElement;
            $checkbox.setAttribute('aria-label', `Apply ${$invoice.textContent ?? 'deposit'} to this order`);
            $checkbox.addEventListener('change', () => {
                if ($checkbox.checked) {
                    skipped.delete(order.id);
                } else {
                    skipped.add(order.id);
                }
                // Dim the row's text so an unchecked deposit reads as
                // excluded at a glance.
                $info.classList.toggle('opacity-50', !$checkbox.checked);
                $amount.classList.toggle('opacity-50', !$checkbox.checked);
                notifySelection();
            });

            f.appendChild($row);
        }
        $list.replaceChildren(f);

        $section.classList.remove('d-none');

        // A re-render resets every row to checked — push the default
        // selection so the summary can't be left holding a stale subset.
        notifySelection();
    }

    return {
        refresh: render,
        setApplied,
        skippedDepositOrderIDs: () => Array.from(skipped),
    };
}
